import { useState, type ComponentPropsWithRef, type ReactNode } from "react";

import { cn } from "../lib/cn";
import { XIcon } from "../lib/icons";

export type AlertTone = "neutral" | "info" | "success" | "warning" | "danger";

/**
 * Le rail coloré, l'icône et le titre viennent de tables explicites plutôt que
 * de classes composées (`bg-${tone}-subtle`) : Tailwind analyse le texte source,
 * une classe assemblée à l'exécution n'est jamais générée.
 */
const RAIL: Record<AlertTone, string> = {
  neutral: "bg-border-strong",
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
};

const CHIP: Record<AlertTone, string> = {
  neutral: "bg-surface-sunken text-fg-muted",
  info: "bg-info-subtle text-info-fg",
  success: "bg-success-subtle text-success-fg",
  warning: "bg-warning-subtle text-warning-fg",
  danger: "bg-danger-subtle text-danger-fg",
};

const TINTED: Record<AlertTone, string> = {
  neutral: "border-border bg-surface-sunken",
  info: "border-info/25 bg-info-subtle",
  success: "border-success/25 bg-success-subtle",
  warning: "border-warning/30 bg-warning-subtle",
  danger: "border-danger/25 bg-danger-subtle",
};

const TITLE: Record<AlertTone, string> = {
  neutral: "text-fg",
  info: "text-info-fg",
  success: "text-success-fg",
  warning: "text-warning-fg",
  danger: "text-danger-fg",
};

/** Tracés d'icônes. Une alerte ne doit jamais reposer sur la seule couleur (WCAG 1.4.1). */
const ICON: Record<AlertTone, ReactNode> = {
  neutral: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  success: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.5 2.5 2.5 4.5-5" />
    </>
  ),
  warning: (
    <>
      <path d="M10.3 4.3 2.8 17a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  danger: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5M12 16h.01" />
    </>
  ),
};

export interface AlertProps extends Omit<ComponentPropsWithRef<"div">, "title"> {
  tone?: AlertTone;
  /**
   * `rail` (défaut) : surface neutre et rail coloré sur le bord gauche.
   * `tinted` : aplat teinté sur toute la surface, pour une alerte qui doit
   * dominer la page.
   */
  variant?: "rail" | "tinted";
  title?: ReactNode;
  /** Actions posées sous le message (bouton « Réessayer », lien…). */
  actions?: ReactNode;
  hideIcon?: boolean;
  /** Affiche une croix de fermeture et appelle ce rappel après l'animation de sortie. */
  onDismiss?: () => void;
}

/**
 * Message contextuel.
 *
 * La variante par défaut reprend l'idiome éditorial du site : un rail de trois
 * pixels sur le bord gauche, comme une citation, plutôt qu'un aplat teinté sur
 * toute la surface. Trois raisons de préférer ce traitement :
 *
 *   - trois alertes empilées restent lisibles, là où trois aplats colorés
 *     transforment la page en gyrophare ;
 *   - le texte reste sur une surface neutre, donc à contraste maximal ;
 *   - la couleur devient un accent d'identification et non une ambiance.
 *
 * `variant="tinted"` reste disponible quand l'alerte doit vraiment dominer.
 *
 * Le rôle ARIA dépend du ton : `alert` pour une erreur (interrompt le lecteur
 * d'écran immédiatement), `status` sinon. Une erreur mérite l'interruption ;
 * une confirmation, non.
 */
export function Alert({
  className,
  tone = "neutral",
  variant = "rail",
  title,
  actions,
  hideIcon,
  onDismiss,
  children,
  ...props
}: AlertProps) {
  const [closing, setClosing] = useState(false);

  const dismiss = () => {
    setClosing(true);
    // On laisse la transition de sortie se jouer avant de retirer l'élément :
    // sinon il disparaît d'un coup et l'utilisateur doute de ce qu'il a cliqué.
    setTimeout(() => onDismiss?.(), 150);
  };

  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cn(
        "relative flex transform-gpu animate-slide-up gap-3 overflow-hidden rounded-xl border p-4 text-sm",
        // Sortie en opacité + translation : le bandeau s'efface sans jamais
        // faire remonter le contenu qui le suit avant la fin de l'animation.
        "transition-[opacity,translate] duration-fast ease-exit",
        variant === "tinted" ? TINTED[tone] : "border-border bg-surface-raised pl-5",
        closing && "pointer-events-none -translate-y-1 opacity-0",
        className,
      )}
      {...props}
    >
      {variant === "rail" && (
        <span aria-hidden="true" className={cn("absolute inset-y-0 left-0 w-[3px]", RAIL[tone])} />
      )}

      {!hideIcon && (
        <span
          aria-hidden="true"
          className={cn(
            "flex size-6 shrink-0 items-center justify-center rounded-full",
            variant === "rail" ? CHIP[tone] : "bg-transparent",
          )}
        >
          <svg
            className="size-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {ICON[tone]}
          </svg>
        </span>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title && (
          <p className={cn("font-semibold", variant === "rail" ? "text-fg" : TITLE[tone])}>
            {title}
          </p>
        )}
        {children && (
          <div className={variant === "rail" ? "text-fg-muted" : "opacity-90"}>{children}</div>
        )}
        {actions && <div className="mt-2 flex flex-wrap gap-2">{actions}</div>}
      </div>

      {onDismiss && (
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fermer"
          className="-m-1 h-fit rounded-sm p-1 text-fg-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <XIcon className="size-4" />
        </button>
      )}
    </div>
  );
}
